'use client';

import { type ReactElement } from 'react';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useRecaptchaVisibleState } from '@/hooks/store';
import { useIsMobile } from '@/hooks/ui/useIsMobile';

import AppleAuthButton from '../AppleAuthButton';
import AuthPromptToggle from '../AuthPromptToggle';
import AuthTerms from '../AuthTerms';
import EmailAuthButton from '../EmailAuthButton';
import FacebookAuthButton from '../FacebookAuthButton';
import GoogleAuthButton from '../GoogleAuthButton';
import MarketingOptIn from '../MarketingOptIn';
import { useSisu } from '../Provider';
import { type MessageNode } from '../utils';
import isFormattedMessage from '../utils/isFormattedMessage';
import { AuthPromptToggleWrapper, Card, HeaderWrapper, SocialSignupWrapper, Wrapper } from './styles';

export type AuthFormProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "sisu-auth-form"
	 * ```
	 */
	'data-testid'?: string;

	/**
	 * Set to `null` to hide.
	 * @defaultValue
	 * ```tsx
	 * <AuthPromptToggle />
	 * ```
	 */
	authPromptToggle?: ReactElement | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * "above"
	 * ```
	 */
	authPromptTogglePosition?: 'above' | 'below';

	/**
	 * Set to `null` to hide.
	 * @defaultValue
	 * ```tsx
	 * <AppleAuthButton />
	 * ```
	 */
	appleAuthButton?: ReactElement | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * <EmailAuthButton />
	 * ```
	 */
	emailAuthButton?: ReactElement | null;

	/**
	 * Set to `null` to hide.
	 * @defaultValue
	 * ```tsx
	 * <FacebookAuthButton />
	 * ```
	 */
	facebookAuthButton?: ReactElement | null;

	/**
	 * Set to `null` to hide.
	 * @defaultValue
	 * ```tsx
	 * <GoogleAuthButton />
	 * ```
	 */
	googleAuthButton?: ReactElement | null;

	/**
	 * Set to `null` to hide.
	 * @defaultValue
	 * ```tsx
	 * <MarketingOptIn />
	 * ```
	 */
	marketingOptIn?: ReactElement | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	title?: MessageNode | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	subtitle?: MessageNode | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * <AuthTerms />
	 * ```
	 */
	authTerms?: ReactElement | null;
};

const AuthFormInternal = ({
	'data-testid': testId = 'sisu-auth-form',

	authPromptToggle = <AuthPromptToggle />,
	authPromptTogglePosition = 'above',

	appleAuthButton = <AppleAuthButton />,
	emailAuthButton = <EmailAuthButton />,
	facebookAuthButton = <FacebookAuthButton />,
	googleAuthButton = <GoogleAuthButton />,

	marketingOptIn = <MarketingOptIn />,

	title = null,

	subtitle = null,

	authTerms = <AuthTerms />,
}: AuthFormProps) => {
	const { authPrompt } = useAuthPrompt();
	const recaptchaVisible = useRecaptchaVisibleState();

	return (
		<Wrapper data-testid={testId}>
			{(title || (authPromptTogglePosition === 'above' && authPromptToggle) || subtitle) && (
				<HeaderWrapper>
					{title && isFormattedMessage(title) ? (
						<Text el="h1" noMargin size={FontSizes.lg} weight={FontWeights.Medium}>
							{title}
						</Text>
					) : (
						title
					)}
					{authPromptTogglePosition === 'above' && authPromptToggle}
					{subtitle && isFormattedMessage(subtitle) ? (
						<Text el="p" noMargin>
							{subtitle}
						</Text>
					) : (
						subtitle
					)}
				</HeaderWrapper>
			)}
			<SocialSignupWrapper>
				<li>{emailAuthButton}</li>
				{facebookAuthButton && <li>{facebookAuthButton}</li>}
				{appleAuthButton && <li>{appleAuthButton}</li>}
				{googleAuthButton && <li>{googleAuthButton}</li>}
			</SocialSignupWrapper>
			{authPromptTogglePosition === 'below' && authPromptToggle && (
				<AuthPromptToggleWrapper>{authPromptToggle}</AuthPromptToggleWrapper>
			)}
			{!recaptchaVisible && authTerms}
			{authPrompt === 'signup' && marketingOptIn}
		</Wrapper>
	);
};

export default function AuthForm(props: AuthFormProps) {
	const [isMobile] = useIsMobile();
	const { isWithinModal } = useSisu();

	if (isMobile && isWithinModal) {
		return <AuthFormInternal {...props} />;
	}

	return (
		<Card isWithinModal={isWithinModal}>
			<AuthFormInternal {...props} />
		</Card>
	);
}
